import { Component, ViewChild } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TableSummaryComponent } from '../table-summary/table-summary.component';
import { AuthService } from 'src/app/shared/services/auth.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent {
  resumo = new FormGroup({
    initialDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
    endDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
  });

  public isSubmitted: boolean = false;
  public isLoaded: boolean = false;

  private accountTimezoneOffsetMinutes: number | null = null;
  private originCurrency: string = 'BRL';

  @ViewChild(TableSummaryComponent) table!: TableSummaryComponent;

  constructor(
    private toastr: ToastrService,
    private authService: AuthService
  ) { }

  private parseTimezoneOffset(tz: string | null | undefined): number | null {
    if (!tz) return null;

    const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(tz.trim());
    if (!m) return null;

    const sign = m[1] === '-' ? 1 : -1;
    const hours = Number(m[2]);
    const minutes = Number(m[3]);

    return sign * (hours * 60 + minutes);
  }

  private nowInAccountTz(): Date {
    const now = new Date();

    if (this.accountTimezoneOffsetMinutes == null) {
      return now;
    }

    const browserOffset = now.getTimezoneOffset();
    const diffMinutes = this.accountTimezoneOffsetMinutes - browserOffset;

    return new Date(now.getTime() - diffMinutes * 60000);
  }

  private buildDefaultRange(hasFlag: boolean): { start: Date; end: Date } {
    const origin = (this.originCurrency || 'BRL').toUpperCase();

    if (origin === 'BRL') {
      const now = new Date();
      const tempEnd = new Date(now);

      const start = hasFlag
        ? (() => {
            const d = new Date(now.getTime() - 15 * 60 * 1000);
            d.setSeconds(0, 0);
            return d;
          })()
        : (() => {
            const d = new Date(now);
            d.setHours(0, 0, 0, 0);
            return d;
          })();

      const end = this.formatSeconds(tempEnd);
      return { start, end };
    }

    const nowAcc = this.nowInAccountTz();
    const tempEnd = new Date(nowAcc);

    const start = hasFlag
      ? (() => {
          const d = new Date(nowAcc.getTime() - 15 * 60 * 1000);
          d.setSeconds(0, 0);
          return d;
        })()
      : (() => {
          const d = new Date(nowAcc);
          d.setHours(0, 0, 0, 0);
          return d;
        })();

    const end = this.formatSeconds(tempEnd);
    return { start, end };
  }

  async ngOnInit(): Promise<void> {
    try {
      const account = await firstValueFrom(this.authService.getAccount());
      const hasFlag = !!account?.featureFlagNewParam;

      this.originCurrency =
        ((account as any)?.originCurrency ||
          this.authService.getOriginCurrency?.() ||
          'BRL').toUpperCase();

      this.accountTimezoneOffsetMinutes = this.parseTimezoneOffset(
        (account as any)?.timezone
      );

      const { start, end } = this.buildDefaultRange(hasFlag);

      this.resumo.controls.initialDate.setValue(start);
      this.resumo.controls.endDate.setValue(end);

      this.buscar();
    } catch {
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      this.resumo.controls.initialDate.setValue(start);
      this.resumo.controls.endDate.setValue(now);
      this.buscar();
    }
  }

  private toLocalIso(v: any): string {
    const dt = this.asDate(v);
    const pad = (n: number) => String(n).padStart(2, '0');
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    const ss = pad(dt.getSeconds());
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
  }

  private toNgb(v: any): NgbDateStruct {
    const d = this.asDate(v);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  buscar() {
    this.isSubmitted = true;
    this.isLoaded = false;

    const start = this.resumo.controls.initialDate.value!;
    const tempEnd = this.resumo.controls.endDate.value!;
    const end = this.formatSeconds(tempEnd);

    if (start > end) {
      this.toastr.error(
        'Data inicial deve ser menor ou igual a data final!',
        'Erro',
        {
          timeOut: 3000,
          positionClass: 'toast-top-right',
        }
      );
      this.isLoaded = true;
      return;
    }

    if (this.resumo.invalid) {
      this.isLoaded = true;
      return;
    }

    this.table.dataInitial = this.toNgb(start);
    this.table.dataFinal = this.toNgb(end);

    this.table.startDateIso = this.toLocalIso(start);
    this.table.endDateIso = this.toLocalIso(end);

    this.table.getTransactions();

    this.isSubmitted = false;
  }

  private formatSeconds(v: any): Date {
    const d = this.asDate(v);
    const copy = new Date(d);
    copy.setSeconds(59, 999);
    return copy;
  }
}
